import {
	Autocomplete,
	Button,
	Input,
	ListBox,
	ListBoxItem,
	Popover,
	SearchField,
	Select as RACSelect,
	SelectValue,
	Text,
	type ListBoxItemProps,
	type SelectProps,
	useFilter,
	SelectStateContext,
	Virtualizer,
	ListLayout,
} from 'react-aria-components';
import styles from './Select.module.css';
import { useContext } from 'react';

export interface ISelectItem {
	/** A unique ID for the item. */
	readonly id: string;
	/** Human-readable display text of the item. */
	readonly text: string;
	/** An optional icon for the item. */
	readonly icon?: string[];
}

interface ISelectProps extends Omit<SelectProps<ISelectItem>, 'children'> {
	readonly placeholder?: string;
	readonly isClearable?: boolean;
	readonly items: Iterable<ISelectItem>;
	readonly searchable?: boolean;
	readonly selectValueIcon?: string;
	readonly popoverFixSize?: boolean;
}

export function Select({
	className,
	isClearable,
	items,
	placeholder,
	searchable,
	selectValueIcon,
	popoverFixSize,
	...props
}: ISelectProps) {
	const state = useContext(SelectStateContext);

	const { contains } = useFilter({ sensitivity: 'base' });
	return (
		<RACSelect<ISelectItem>
			className={`${styles.select} ${className || ''}`}
			placeholder={typeof placeholder === 'undefined' ? '' : placeholder}
			{...props}
		>
			<div className={styles.selectContainer}>
				<Button
					data-copid='openButton'
					className={styles.selectButton}
				>
					{
						selectValueIcon 
							? <div className={styles.selectIcon}>
								<i className={selectValueIcon}/>	
							</div>
							: <>
								<SelectValue
									className={styles.selectValue}
								/>
								<span
									aria-hidden={true}
									className={styles.selectArrow}
								>
									<i
										className={'fa fa-caret-down'}
									/>
								</span>
							</>	
					}					
				</Button>
				
				{isClearable &&
					state &&
						state.selectedKey !== null && (
					<Button
						aria-label={'Clear'}
						slot={null}
						className={styles.buttonClear}
						onPress={() => state?.setSelectedKey(null)}
						preventFocusOnPress={true}
					>
						<i
							className={'fa fa-close'}
						/>
					</Button>
				)}
			</div>
			<Popover
				className={`${styles.popover} ${!!popoverFixSize ? styles.popoverFixSize : ''} ${!!selectValueIcon ? styles.popoverMinWidth : ''}`}
				offset={3}
				containerPadding={0}
				maxHeight={300}
			>
				<Autocomplete<ISelectItem> filter={contains}>
					{
						searchable &&
							<SearchField
								aria-label={'Search...'}
								autoFocus={true}
								className={styles.searchField}
							>
								<Input
									placeholder={'Search...'}
									className={styles.searchInput}
								/>
								<Button
									data-copid='clearSearchButton'
									className={styles.clearSearchButton}
								>
									<i
										className={'fa fa-close'}
									/>
								</Button>
							</SearchField>
					}
					<Virtualizer
						layout={ListLayout}
						layoutOptions={{
							rowHeight: 30,
						}}
					>
						<ListBox<ISelectItem>
							className={styles.listbox}
							items={items}
						>
							{(item) => <SelectItem textValue={item.text} />}
						</ListBox>
					</Virtualizer>
				</Autocomplete>
			</Popover>
		</RACSelect>
	);
}

function SelectItem({ children, ...props }: ListBoxItemProps<ISelectItem>) {
	return (
		<ListBoxItem
			className={({ isFocused, isSelected }) =>
				`${styles.selectItem} ${isFocused ? 'focused' : ''} ${isSelected ? 'selected' : ''}`
			}
			data-copid='selectItem'
			{...props}
		>
			{props.value?.icon && props.value?.icon.map(icon => {
				if (!icon) {
					return null;
				}
				return (
					<img
						alt={props.value?.text}
						className={icon.indexOf('shield') > -1 ? styles.itemIcon : styles.itemIconFlag}
						src={icon}
					/>
				);
			})}
			<Text
				slot='label'
				className={styles.itemText}
				title={props.value?.text}
			>
				{props.value?.text}
			</Text>
		</ListBoxItem>
	);
}
